import React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LazyImage from "@insite/mobius/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import getColor from "@insite/mobius/utilities/getColor";

const enum fields {
    title = "title",
    acrowConcreteSystemItem = "acrowConcreteSystemItem",
    buttonText = "buttonText",
    openInNewWindow="openInNewWindow",
    link = "link",
    lastImage = "lastImage",
}

export interface AcrowConcreteSystemStyleModel {
    fields: {
        mainImage: string;
        caption: string;
        content: string;
        buttonText?: string;
        openInNewWindow?: boolean;
        link?: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.buttonText]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
        [fields.lastImage]: string;
        [fields.acrowConcreteSystemItem]: AcrowConcreteSystemStyleModel[];
    };
}

export interface AcrowConcreteSystemStyles {
    mainContainer?: InjectableCss;
    titleText?: TypographyProps;
    infoGridItem?: GridItemProps;
    container?: InjectableCss;
    boxContainer?: InjectableCss;
    caption?: TypographyProps;
    info?: TypographyProps;
    imageContainer?: InjectableCss;
    button?: InjectableCss;
}

const acrowConcreteSystemStyle: AcrowConcreteSystemStyles = {
    mainContainer: {
        css: css`
            color: ${getColor("text.main")};
        `,
    },
    titleText: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 24px;
            font-weight: 500;
            line-height: 32px;
            letter-spacing: 0;
            text-align: center;
            color: ${getColor("text.main")};
            text-transform: uppercase;
            padding-bottom: 25px;
            margin: 0;
            margin-bottom: 5px;
        `,
    },
    infoGridItem: {
        css: css`
            min-height: 540px;
        `,
    },
    container: {
        css: css`
            background-color:  ${getColor("primary.contrast")};
            padding: 2px 2px 40px;
            height: 100%;
            width: 100%;
        `,
    },
    boxContainer: {
        css: css`
            box-sizing: border-box;
            height: 100%;
            text-align: center;
            padding: 0 8px;
            transition: 0.2s all ease;
        `,
    },
    caption: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            line-height: 28px;
            letter-spacing: 0;
            text-align: center;
        `,
    },

    info: {
        css: css`
            font-size: 14px;
            font-weight: 400;
            line-height: 19px;
            letter-spacing: 0;
            text-align: left;
            margin-bottom: 0;
            padding: 7px;
        `,
    },

    imageContainer: {
        css: css`
            margin-left: 10%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                ])}
        `,
    },
    button: {
        css: css`
            margin-top: 20px;
            color:  ${getColor("primary.contrast")} !important;
            transition: 0.3s;
            max-width: 100%;
            word-wrap: break-word;
            background: ${getColor("primary.main")};
            border: 1px solid ${getColor("primary.main")};
            color: ${getColor("text.main")} !important;
            cursor: pointer;
            display: inline-block;
            font-weight: 500;
            box-sizing: border-box;
            vertical-align: middle;
            padding: 14px 17px;
            border-radius: 0px;
            box-shadow: none;
            width: 100%;
            ${TypographyStyle} {
                color: ${getColor("primary.contrast")};
                font-style: normal;
                font-weight: 500;
                line-height: 1.42857143;
                font-size: 21px;
                line-height: 27px;
            }
            &:hover {
                background: ${getColor("primary.contrast")};
                ${TypographyStyle} {
                    color: ${getColor("primary.main")};
                }
            }
        `,
    },
};

const styles = acrowConcreteSystemStyle;

// New: item-level subcomponent so useGetLink can be called per item legally
const AcrowConcreteSystemGridItem = ({ item }: { item: AcrowConcreteSystemStyleModel }) => {
    const itemLink = useGetLink(item.fields.link ?? { type: "Page", value: "" });

    return (
        <GridItem width={[12, 12, 4, 4, 4]} {...styles.infoGridItem} key={item.fields.caption}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.boxContainer}>
                    <StyledWrapper>
                        <LazyImage src={item.fields.mainImage} />
                        <Typography as="div" {...styles.caption}>
                            {item.fields.caption}
                        </Typography>
                        <Typography variant="p" {...styles.info}>
                            {item.fields.content}
                        </Typography>
                        {item.fields.buttonText && item.fields.link && (
                            <Link href={itemLink.url}   target={item.fields.openInNewWindow ? "_blank" : ""} {...styles.button}>
                                {item.fields.buttonText}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </GridItem>
    );
};

const AcrowConcreteSystem = ({ fields }: OwnProps) => {
    const link = useGetLink(fields.link);
    return (
        <StyledWrapper {...styles.mainContainer}>
            <Typography {...styles.titleText}>{fields.title}</Typography>
            <GridContainer>
                {fields.acrowConcreteSystemItem &&
                    fields.acrowConcreteSystemItem.map(item => (
                        <AcrowConcreteSystemGridItem item={item} key={item.fields.caption} />
                    ))}
                <GridItem width={[12, 12, 4, 4, 4]} {...styles.infoGridItem}>
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.boxContainer}>
                            <StyledWrapper>
                                <LazyImage src={fields.lastImage} />
                                {fields.buttonText && link && (
                                    <Link href={link.url} {...styles.button}>
                                        {fields.buttonText}
                                    </Link>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const AcrowConcreteSystemModule: WidgetModule = {
    component: AcrowConcreteSystem,
    definition: {
        displayName: "Acrow Concrete System Solution",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "link",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "lastImage",
                displayName: "Last Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.acrowConcreteSystemItem,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.caption;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "mainImage",
                        displayName: "Main Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "caption",
                        displayName: "Caption",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "content",
                        displayName: "Content",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonText",
                        displayName: "Button Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default AcrowConcreteSystemModule;